import { Divider, Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import React, { createContext, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styledComponent from 'styled-components';
import { ExtendDuration } from './ExtendDuration';
import { Miscellaneous } from './MiscellaneousFields';
import { ExtensionTimeOff } from './ExtensionTimeOff';
import { GetMomentOfDate } from 'utils/dates/moment';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectMiscellaneousPlacementData,
  selectPlacementHeaderData,
} from 'store/redux-store/placement-details/selectors';
import { Moment } from 'moment';
import { selectCueRecipient } from 'app/components/Tasks/store/Tasks.selectors';
import { ICueRecipient } from 'app/models/Tasks/Tasks';
import { Concatenate } from 'utils/string/string';
import { placementStatusAction } from 'store/redux-store/placement-status/slice';
import { selectUser } from 'oidc/user.selectors';
import { getExtensionEndDate } from './helper';
import { validationSchema } from './extensionValidation';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  ExtEditTarget,
  isFieldEditableForExtensionId,
} from '../PlacementDetails/PlacementTabPanel/PlacementSummaryTab/Statuses/Extensions/ExtensionEditIcon';
import { selectExtensionInfo } from 'store/redux-store/placement-summary/selectors';
import { convertToFormat } from 'app/helpers/dateHelper';
import { getExtensionDuration } from 'app/helpers/getExtensionDuration';
import { placementDetailsAction } from 'store/redux-store/placement-details/slice';

const extractRecipients = (recipients: ICueRecipient[] = []) =>
  recipients.map(item => ({
    name: Concatenate([item.firstName, item.lastName], ' '),
    value: item.employeeId,
  }));

export const ExtensionContext = createContext<{
  isNewExtension: boolean;
  startDateMoment?: Moment;
  cueRecipients?: {
    name: string;
    value: number;
  }[];
  timeOffPayerOpts?: {
    name: string;
    value: number;
  }[];
  disabledFields;
  currentTargetField?: ExtEditTarget | null;
}>({
  disabledFields: { startDate: true },
  isNewExtension: true,
});

const DEFAULT_DURATION = 13;

const useStyles = makeStyles()(theme => ({
  extendDurationHeading: {
    margin: '10px auto',
  },
}));

const isDatesEditable = (approvalStatusId, extensionOverride) =>
  (approvalStatusId === 4 && extensionOverride === 4) || (approvalStatusId === 4 && extensionOverride === 6);

const formatExtensionDates = ({ startDate, endDate }) => ({
  startDate: convertToFormat(startDate, 'yyyy-MM-DDTHH:mm:ss', false),
  endDate: convertToFormat(endDate, 'yyyy-MM-DDTHH:mm:ss', false),
});

export const ExtensionForm = ({
  isNewExtension,
  extensionStatus,
  extensionStatusId,
  formBtnAccessorId,
  extensionOverride,
  endDateTravelerOption,
  extContainerIndex,
  currentTargetField,
}) => {
  const { t } = useTranslation();
  const { classes } = useStyles();

  const {
    placementId,
    placementDate: { endDate: oldEndDate },
    accountManagerId,
  } = useSelector(selectPlacementHeaderData);

  const currentExtension = useSelector(selectExtensionInfo);

  const [startDateMoment] = useState(() =>
    isNewExtension
      ? GetMomentOfDate(oldEndDate).add(1, 'd')
      : GetMomentOfDate(currentExtension[extContainerIndex]?.startDate),
  );

  const [endDateMoment] = useState(() =>
    endDateTravelerOption
      ? GetMomentOfDate(endDateTravelerOption.endDate)
      : getExtensionEndDate(startDateMoment, DEFAULT_DURATION),
  );

  /** Extract cueRecipients dropdown values  */
  const recipientList = useSelector(selectCueRecipient);
  const cueRecipients = React.useMemo<{ name: string; value: number }[]>(
    () => [{ name: 'No Cue', value: Number.MIN_VALUE }, ...extractRecipients(recipientList)],
    [recipientList],
  );

  /** Extract extTimeOffPayerOpts */
  const { extTimeOffPayerOpts } = useSelector(selectMiscellaneousPlacementData);
  const timeOffPayerOpts = React.useMemo<{ name: string; value: number }[]>(
    () => extTimeOffPayerOpts.map(opt => ({ name: opt.description, value: opt.id })),
    [extTimeOffPayerOpts],
  );

  const [disabledFields] = useState(() =>
    !startDateMoment.isValid()
      ? {
          startDate: true,
          duration: true,
          endDate: true,
          timeOffRequest: true,
          amnHousing: true,
          notes: true,
          isSentCueNote: true,
          cueRecipient: true,
        }
      : isNewExtension || isFieldEditableForExtensionId(extensionStatusId)
      ? {
          startDate: true,
          duration: isNewExtension && isDatesEditable(endDateTravelerOption?.approvalStatusId, extensionOverride),
          endDate: isNewExtension && isDatesEditable(endDateTravelerOption?.approvalStatusId, extensionOverride),
        }
      : {
          startDate: true,
          duration: true,
          endDate: true,
          timeOffRequest: true,
          amnHousing: true,
        },
  );

  const {
    userInfo: { employeeId },
  } = useSelector(selectUser);

  const dispatch = useDispatch();

  const formMethods = useForm({
    defaultValues: isNewExtension
      ? {
          duration: !startDateMoment.isValid() ? 0 : getExtensionDuration(startDateMoment, endDateMoment.toDate()),
          startDate: startDateMoment.toDate(),
          endDate: !startDateMoment.isValid() ? null : endDateMoment.toDate(),
          timeOffRequest: {
            timeOffRequestReason: 1,
            timeOffRequestDetails: [],
          },
          notes: null,
          amnHousing: false,
          isSentCueNote: true,
          cueRecipient: cueRecipients.some(item => item.value === accountManagerId)
            ? accountManagerId
            : Number.MIN_VALUE,
        }
      : {
          duration: currentExtension[extContainerIndex]?.duration,
          startDate: currentExtension[extContainerIndex]?.startDate,
          endDate: currentExtension[extContainerIndex]?.endDate,
          timeOffRequest: currentExtension[extContainerIndex]?.timeOffRequest
            ? {
                timeOffRequestReason: currentExtension[extContainerIndex].timeOffRequest.timeOffRequestReason,
                timeOffRequestDetails: currentExtension[extContainerIndex].timeOffRequest.timeOffRequestDetails.map(
                  item => ({
                    ...item,
                    startDate: item.startDate,
                    endDate: item.endDate,
                  }),
                ),
              }
            : null,
          notes: currentExtension[extContainerIndex]?.notes,
          amnHousing: !!currentExtension[extContainerIndex]?.amnHousing,
          isSentCueNote: true,
          cueRecipient: cueRecipients.some(item => item.value === accountManagerId)
            ? accountManagerId
            : Number.MIN_VALUE,
        },
    resolver: yupResolver(validationSchema),
  });

  const { handleSubmit } = formMethods;

  const onSubmit = data =>
    isNewExtension
      ? dispatch(
          placementStatusAction.updateExtentionStatusDetails({
            ...data,
            ...formatExtensionDates({ ...data }),
            placementId,
            extensionStatusId,
            extensionStatus,
            skipToStatusId: extensionOverride,
            timeOffRequest: {
              timeOffRequestDetails:
                Number.parseInt(data.timeOffRequest.timeOffRequestReason) === 1
                  ? data.timeOffRequest?.timeOffRequestDetails.map(item => ({
                      ...item,
                      placementTimeOffId: 0,
                    }))
                  : [],
              timeOffRequestReason: Number.parseInt(data.timeOffRequest.timeOffRequestReason ?? 0),
            },
            cueNewOrUpdate: 'New',
            cueDetail: {
              senderId: employeeId,
              recipientId: data.cueRecipient === Number.MIN_VALUE ? null : data.cueRecipient,
            },
            userId: employeeId,
          }),
        )
      : dispatch(
          placementStatusAction.updateExtentionStatusDetails({
            placementId,
            userId: employeeId,
            extensions: currentExtension.map((item, index) =>
              index === extContainerIndex
                ? {
                    isEdited: true,
                    extensionStatusId,
                    extensionStatus: item.extensionStatus,
                    skipToStatusId: extensionOverride,
                    ...data,
                    ...formatExtensionDates({ ...data }),
                    cueNewOrUpdate: 'Update',
                    cueDetail: {
                      senderId: employeeId,
                      recipientId: data.cueRecipient === Number.MIN_VALUE ? null : data.cueRecipient,
                    },
                    timeOffRequest: {
                      timeOffRequestDetails:
                        Number.parseInt(data.timeOffRequest.timeOffRequestReason) === 1
                          ? data.timeOffRequest?.timeOffRequestDetails.map(item => ({
                              ...item,
                              placementTimeOffId: item.placementTimeOffId < 0 ? 0 : item.placementTimeOffId,
                            }))
                          : [],
                      timeOffRequestReason: Number.parseInt(data.timeOffRequest.timeOffRequestReason ?? 0),
                    },
                  }
                : {
                    ...item,
                  },
            ),
          }),
        );

  React.useEffect(() => {
    dispatch(placementDetailsAction.getExtTimeOffPayerOpts());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ExtensionContext.Provider
      value={{
        startDateMoment,
        cueRecipients,
        timeOffPayerOpts,
        disabledFields,
        currentTargetField,
        isNewExtension,
      }}
    >
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container direction="column">
            <Grid item>
              <BoldDiv className={`subtitle2 ${classes.extendDurationHeading}`}>{t('Extend Assignment')}</BoldDiv>
              <ExtendDuration />
            </Grid>
            <CustomDivider style={{ marginTop: 0 }} />
            <Grid item>
              <BoldDiv className="subtitle2">{t('Requested Time Off')}</BoldDiv>
              <ExtensionTimeOff />
            </Grid>
            <CustomDivider />
            <Grid item>
              <BoldDiv className="subtitle2">{t('Other')}</BoldDiv>
              <Miscellaneous />
            </Grid>
          </Grid>
          <button id={formBtnAccessorId} type="submit" style={{ visibility: 'hidden' }} />
        </form>
      </FormProvider>
    </ExtensionContext.Provider>
  );
};

const BoldDiv = styledComponent.div`
  font-weight: 450;
`;

const CustomDivider = styledComponent(Divider)`
  margin: 10px 0;
`;
